import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SparklesIcon, SearchIcon, Loader2Icon, DatabaseIcon, TargetIcon, PenLineIcon, CheckCircle2Icon, FileSpreadsheetIcon, XIcon, ChevronLeftIcon, ChevronRightIcon, PencilIcon, FileIcon, FolderIcon, ArrowRightIcon, ArrowLeftIcon, ChevronDownIcon, ClockIcon, SettingsIcon, SendIcon, UsersIcon, MailIcon, PhoneIcon, ExternalLinkIcon, DownloadIcon, ThumbsUpIcon, ThumbsDownIcon, CopyIcon } from 'lucide-react';
const STEP_DURATION = 15000;
const steps = [{
  id: 1,
  title: "Describe what you're looking for",
  description: 'Type a natural language query describing your ideal customer. No complex filters needed.',
  icon: SearchIcon
}, {
  id: 2,
  title: 'Answer a few quick questions',
  description: 'The agent asks clarifying questions about revenue, company size, and funding stage to refine your search.',
  icon: TargetIcon
}, {
  id: 3,
  title: 'Review scored companies',
  description: 'Each lead is scored across 4 dimensions with weighted priorities so your highest-value leads surface first.',
  icon: DatabaseIcon
}, {
  id: 4,
  title: 'Get decision-maker contacts',
  description: 'Access verified owner and manager-level contacts with direct emails and phone numbers.',
  icon: UsersIcon
}, {
  id: 5,
  title: 'Send personalized outreach',
  description: 'AI-drafted emails reference specific data points about each lead for maximum relevance.',
  icon: MailIcon
}];
function ChatWindow({
  children,
  inputPlaceholder = 'Ask a follow-up question...',
  inputValue = ''
}: {
  children: React.ReactNode;
  inputPlaceholder?: string;
  inputValue?: string;
}) {
  return <div className="bg-slate-50 rounded-2xl border border-slate-200 shadow-xl overflow-hidden flex flex-col h-[480px] w-full max-w-2xl mx-auto" data-id="element-5612">
      <div className="px-4 py-3 border-b border-stone-200/50 flex items-center justify-between bg-white/50" data-id="element-5613">
        <div className="flex items-center gap-2" data-id="element-5614">
          <span className="text-sm font-semibold text-stone-800" data-id="element-5615">
            Sales lead list - retail SMBs
          </span>
          <ChevronRightIcon className="w-4 h-4 text-stone-400 rotate-90" data-id="element-5616" />
        </div>
      </div>
      <div className="p-5 space-y-6 flex-1 overflow-y-auto" data-id="element-5617">{children}</div>
      <div className="p-4 border-t border-stone-200/50 bg-white/50" data-id="element-5618">
        <div className="relative" data-id="element-5619">
          <input type="text" placeholder={inputPlaceholder} value={inputValue} className="w-full pl-4 pr-12 py-3 rounded-xl border border-stone-200 bg-white text-sm focus:outline-none placeholder:text-stone-400" readOnly data-id="element-5620" />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-stone-100 rounded-lg flex items-center justify-center" data-id="element-5621">
            <ArrowLeftIcon className="w-4 h-4 text-stone-400 rotate-90" data-id="element-5622" />
          </div>
        </div>
      </div>
    </div>;
}
function UserMessage({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="flex justify-end" data-id="element-5623">
      <div className="bg-stone-800 text-white rounded-2xl rounded-tr-sm px-5 py-3.5 text-sm max-w-[85%] leading-relaxed shadow-sm" data-id="element-5624">
        {children}
      </div>
    </div>;
}
function AgentMessage({
  children,
  actionLine
}: {
  children: React.ReactNode;
  actionLine?: string;
}) {
  return <div className="space-y-3" data-id="element-5625">
      {actionLine && <div className="flex items-center gap-2 text-xs text-stone-500 pl-1" data-id="element-5626">
          <ChevronRightIcon className="w-3.5 h-3.5" data-id="element-5627" />
          {actionLine}
        </div>}
      <div className="flex items-start gap-3" data-id="element-5628">
        <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center shrink-0 mt-0.5" data-id="element-5629">
          <SparklesIcon className="w-3.5 h-3.5 text-blue-600" data-id="element-5630" />
        </div>
        <div className="flex-1 space-y-3" data-id="element-5631">
          {children}
          <div className="flex items-center gap-2 pt-2" data-id="element-5632">
            <button className="p-1.5 text-stone-400 hover:text-stone-600 rounded-md hover:bg-stone-100 transition-colors" data-id="element-5633">
              <CopyIcon className="w-3.5 h-3.5" data-id="element-5634" />
            </button>
            <button className="p-1.5 text-stone-400 hover:text-stone-600 rounded-md hover:bg-stone-100 transition-colors" data-id="element-5635">
              <ThumbsUpIcon className="w-3.5 h-3.5" data-id="element-5636" />
            </button>
            <button className="p-1.5 text-stone-400 hover:text-stone-600 rounded-md hover:bg-stone-100 transition-colors" data-id="element-5637">
              <ThumbsDownIcon className="w-3.5 h-3.5" data-id="element-5638" />
            </button>
          </div>
        </div>
      </div>
    </div>;
}
function Step1Illustration() {
  return <ChatWindow inputValue="Find hair salons in San Francisco with 2+ locations|" data-id="element-5639">
      <div className="h-full flex items-center justify-center flex-col text-center space-y-4 opacity-50" data-id="element-5640">
        <div className="w-12 h-12 bg-white rounded-2xl shadow-sm flex items-center justify-center border border-stone-200" data-id="element-5641">
          <SparklesIcon className="w-6 h-6 text-blue-600" data-id="element-5642" />
        </div>
        <div data-id="element-5643">
          <h3 className="text-sm font-semibold text-stone-800" data-id="element-5644">
            How can I help you prospect today?
          </h3>
          <p className="text-xs text-stone-500 mt-1" data-id="element-5645">
            Describe your ideal customer profile
          </p>
        </div>
      </div>
    </ChatWindow>;
}
function Step2Illustration() {
  return <ChatWindow data-id="element-5646">
      <UserMessage data-id="element-5647">
        Find hair salons in San Francisco with 2+ locations
      </UserMessage>
      <AgentMessage actionLine="Analyzing query & checking database..." data-id="element-5648">
        <p className="text-sm text-stone-800 leading-relaxed" data-id="element-5649">
          I can help with that. To refine your search and find the best matches,
          I have a quick question:
        </p>
        <div className="bg-white rounded-xl border border-stone-200 p-4 shadow-sm mt-3" data-id="element-5650">
          <p className="text-sm font-semibold text-stone-800 mb-3" data-id="element-5651">
            What's their estimated current revenue?
          </p>
          <div className="space-y-2" data-id="element-5652">
            {['Under $1M', '$1M–$10M', '$10M–$50M'].map((opt, i) => <div key={i} className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${i === 1 ? 'bg-blue-50 border-blue-200' : 'bg-white border-stone-200 hover:bg-stone-50'}`} data-id="element-5653">
                <div className={`w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-bold ${i === 1 ? 'bg-blue-600 text-white' : 'bg-stone-100 text-stone-500'}`} data-id="element-5654">
                  {i + 1}
                </div>
                <span className={`text-sm ${i === 1 ? 'text-blue-900 font-medium' : 'text-stone-600'}`} data-id="element-5655">
                  {opt}
                </span>
              </div>)}
          </div>
        </div>
      </AgentMessage>
    </ChatWindow>;
}
function Step3Illustration() {
  return <ChatWindow data-id="element-5656">
      <UserMessage data-id="element-5657">
        Find hair salons in San Francisco with 2+ locations. Revenue: $1M–$10M.
      </UserMessage>
      <AgentMessage actionLine="Ran 3 commands, scored 28 companies >" data-id="element-5658">
        <p className="text-sm text-stone-800 leading-relaxed" data-id="element-5659">
          All done! I found <strong data-id="element-5660">28 qualified leads</strong> matching your
          criteria. Here are the top results scored by revenue potential and
          intent:
        </p>
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden mt-3" data-id="element-5661">
          <div className="overflow-x-auto" data-id="element-5662">
            <table className="w-full text-sm text-left" data-id="element-5663">
              <thead data-id="element-5664">
                <tr className="border-b border-stone-100 bg-stone-50/50" data-id="element-5665">
                  <th className="px-4 py-2.5 font-semibold text-stone-500" data-id="element-5666">
                    Company
                  </th>
                  <th className="px-4 py-2.5 font-semibold text-stone-500" data-id="element-5667">
                    Domain
                  </th>
                  <th className="px-4 py-2.5 font-semibold text-stone-500" data-id="element-5668">
                    Size
                  </th>
                  <th className="px-4 py-2.5 font-semibold text-stone-500 text-right" data-id="element-5669">
                    Score
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100" data-id="element-5670">
                {[{
                name: 'Supercuts',
                domain: 'supercuts.com',
                size: '50–200',
                score: 94
              }, {
                name: 'Sephora',
                domain: 'sephora.com',
                size: '200+',
                score: 91
              }, {
                name: 'SmartStyle',
                domain: 'smartstyle.com',
                size: '50–200',
                score: 87
              }].map((lead, i) => <tr key={i} className="hover:bg-stone-50/50 transition-colors" data-id="element-5671">
                    <td className="px-4 py-2.5 font-medium text-stone-800" data-id="element-5672">
                      {lead.name}
                    </td>
                    <td className="px-4 py-2.5 text-stone-500" data-id="element-5673">
                      {lead.domain}
                    </td>
                    <td className="px-4 py-2.5 text-stone-500" data-id="element-5674">{lead.size}</td>
                    <td className="px-4 py-2.5 text-right" data-id="element-5675">
                      <span className={`inline-flex items-center justify-center px-2.5 py-1 rounded-lg text-xs font-bold ${lead.score >= 90 ? 'bg-blue-100 text-blue-700' : 'bg-blue-50 text-blue-600'}`} data-id="element-5676">
                        {lead.score}
                      </span>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </AgentMessage>
    </ChatWindow>;
}
function Step4Illustration() {
  return <ChatWindow data-id="element-5677">
      <UserMessage data-id="element-5678">Get decision-maker contacts for the top leads.</UserMessage>
      <AgentMessage actionLine="Enriched 28 companies, found 42 contacts >" data-id="element-5679">
        <p className="text-sm text-stone-800 leading-relaxed" data-id="element-5680">
          Here are the verified owner and manager-level contacts for your top
          leads:
        </p>
        <div className="space-y-2 mt-3" data-id="element-5681">
          {[{
          name: 'Sarah Chen',
          title: 'Owner & Founder',
          company: 'Salon Luxe SF',
          email: 'sarah@salonluxesf.com'
        }, {
          name: 'Michael Rodriguez',
          title: 'General Manager',
          company: 'Beauty Bar Downtown',
          email: 'm.rodriguez@beautybar.com'
        }].map((contact, i) => <div key={i} className="p-3 rounded-xl border border-stone-200 bg-white shadow-sm flex items-start gap-3" data-id="element-5682">
              <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center shrink-0" data-id="element-5683">
                <span className="text-xs font-bold text-blue-700" data-id="element-5684">
                  {contact.name.split(' ').map(n => n[0]).join('')}
                </span>
              </div>
              <div className="flex-1 min-w-0" data-id="element-5685">
                <div className="text-sm font-bold text-stone-800" data-id="element-5686">
                  {contact.name}
                </div>
                <div className="text-xs text-stone-500" data-id="element-5687">
                  {contact.title} · {contact.company}
                </div>
                <div className="flex items-center gap-1.5 text-xs text-stone-600 mt-1.5" data-id="element-5688">
                  <MailIcon className="w-3 h-3 text-stone-400" data-id="element-5689" />
                  {contact.email}
                </div>
              </div>
              <CheckCircle2Icon className="w-4 h-4 text-emerald-500 shrink-0" data-id="element-5690" />
            </div>)}
        </div>
      </AgentMessage>
    </ChatWindow>;
}
function Step5Illustration() {
  return <ChatWindow data-id="element-5691">
      <UserMessage data-id="element-5692">
        Draft personalized outreach emails for these contacts.
      </UserMessage>
      <AgentMessage actionLine="Generated 28 personalized emails >" data-id="element-5693">
        <p className="text-sm text-stone-800 leading-relaxed" data-id="element-5694">
          I've drafted personalized emails for each lead referencing their
          specific data points. Here's a preview for Sarah Chen:
        </p>
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-4 mt-3" data-id="element-5695">
          <div className="space-y-2 mb-3" data-id="element-5696">
            <div className="flex items-center gap-2 text-sm" data-id="element-5697">
              <span className="font-semibold text-stone-400 w-12" data-id="element-5698">To:</span>
              <span className="text-stone-800" data-id="element-5699">sarah@salonluxesf.com</span>
            </div>
            <div className="flex items-center gap-2 text-sm" data-id="element-5700">
              <span className="font-semibold text-stone-400 w-12" data-id="element-5701">
                Subject:
              </span>
              <span className="text-stone-800" data-id="element-5702">
                Quick question about Salon Luxe SF's scheduling
              </span>
            </div>
          </div>
          <div className="border-t border-stone-100 pt-3 text-sm text-stone-700 leading-relaxed space-y-2" data-id="element-5703">
            <p data-id="element-5704">Hi Sarah,</p>
            <p data-id="element-5705">
              I noticed Salon Luxe SF has grown to 3 locations across San
              Francisco—congrats on the expansion! I'm reaching out because many
              multi-location salons we work with struggle with coordinating
              schedules...
            </p>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-3 p-3 rounded-xl border border-stone-200 bg-white shadow-sm" data-id="element-5706">
          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0" data-id="element-5707">
            <FileSpreadsheetIcon className="w-5 h-5 text-blue-600" data-id="element-5708" />
          </div>
          <div className="flex-1 min-w-0" data-id="element-5709">
            <div className="text-sm font-semibold text-stone-800 truncate" data-id="element-5710">
              personalized_outreach_emails.xlsx
            </div>
            <div className="text-xs text-stone-500" data-id="element-5711">Spreadsheet · XLSX</div>
          </div>
          <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-stone-200 text-xs font-semibold text-stone-700 hover:bg-stone-50 transition-colors" data-id="element-5712">
            <DatabaseIcon className="w-3.5 h-3.5 text-green-600" data-id="element-5713" />
            Google Drive
          </button>
        </div>
      </AgentMessage>
    </ChatWindow>;
}
const illustrations = [Step1Illustration, Step2Illustration, Step3Illustration, Step4Illustration, Step5Illustration];
export function OpenclawWorkflow() {
  const [activeStep, setActiveStep] = useState(1);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const progressRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (progressRef.current) clearInterval(progressRef.current);
    setProgress(0);
    const startTime = Date.now();
    progressRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      setProgress(Math.min(elapsed / STEP_DURATION * 100, 100));
    }, 50);
    timerRef.current = setTimeout(() => {
      setActiveStep(prev => prev === steps.length ? 1 : prev + 1);
    }, STEP_DURATION);
  }, []);
  useEffect(() => {
    startTimer();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (progressRef.current) clearInterval(progressRef.current);
    };
  }, [activeStep, startTimer]);
  const handleStepClick = (stepId: number) => {
    setActiveStep(stepId);
  };
  const ActiveIllustration = illustrations[activeStep - 1];
  return <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-5714">
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 md:p-12 lg:p-16 relative overflow-hidden" data-id="element-5715">
        <div className="max-w-3xl mb-16" data-id="element-5716">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 tracking-tight" data-id="element-5717">
            How it works
          </h2>
          <p className="text-lg text-slate-500 leading-relaxed" data-id="element-5718">
            From a simple prompt to personalized outreach, the AI agent handles
            every step of your prospecting workflow.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center" data-id="element-5719">
          {/* Left: Steps Nav */}
          <div className="lg:col-span-4 space-y-3 relative" data-id="element-5720">
            <div className="absolute left-[27px] top-10 bottom-10 w-0.5 bg-slate-100 hidden lg:block" data-id="element-5721" />
            {steps.map(step => {
            const isActive = activeStep === step.id;
            return <button key={step.id} onClick={() => handleStepClick(step.id)} className={`w-full text-left p-5 rounded-2xl transition-all duration-300 relative z-10 ${isActive ? 'bg-white shadow-lg shadow-slate-200/50 ring-1 ring-slate-200/50' : 'hover:bg-slate-50'}`} data-id="element-5722">
                  <div className="flex gap-4" data-id="element-5723">
                    <div className="relative mt-1 shrink-0" data-id="element-5724">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${isActive ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-400'}`} data-id="element-5725">
                        {step.id}
                      </div>
                    </div>
                    <div data-id="element-5726">
                      <h3 className={`text-lg font-bold mb-1 ${isActive ? 'text-slate-900' : 'text-slate-500'}`} data-id="element-5727">
                        {step.title}
                      </h3>
                      <AnimatePresence initial={false} data-id="element-5728">
                        {isActive && <motion.div initial={{
                      height: 0,
                      opacity: 0
                    }} animate={{
                      height: 'auto',
                      opacity: 1
                    }} exit={{
                      height: 0,
                      opacity: 0
                    }} transition={{
                      duration: 0.3
                    }} className="overflow-hidden" data-id="element-5729">
                            <p className="text-sm text-slate-500 leading-relaxed pt-2 pb-4" data-id="element-5730">
                              {step.description}
                            </p>
                            <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden" data-id="element-5731">
                              <div className="h-full bg-blue-600 rounded-full transition-none" style={{
                          width: `${progress}%`
                        }} data-id="element-5732" />
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </div>
                  </div>
                </button>;
          })}
          </div>

          {/* Right: Visual Area */}
          <div className="lg:col-span-8" data-id="element-5733">
            <div className="relative rounded-3xl bg-slate-50 border border-slate-100 p-6 md:p-10 overflow-hidden min-h-[500px] flex items-center justify-center" data-id="element-5734">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-blue-400/[0.08] blur-[100px] rounded-full pointer-events-none" data-id="element-5735" />
              <div className="relative z-10 w-full flex justify-center" data-id="element-5736">
                <AnimatePresence mode="wait" data-id="element-5737">
                  <motion.div key={activeStep} initial={{
                  opacity: 0,
                  y: 20,
                  scale: 0.97
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  y: -20,
                  scale: 0.97
                }} transition={{
                  duration: 0.4,
                  ease: [0.25, 0.46, 0.45, 0.94]
                }} className="w-full" data-id="element-5738">
                    <ActiveIllustration data-id="element-5739" />
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}